import { useEffect, useRef, useState } from "react";

const reasons = [
  {
    icon: "ri-award-line",
    title: "Experienced Professionals",
    description:
      "Our credit experts have a deep understanding of credit laws, regulations, and best practices to ensure effective and ethical credit repair services.",
  },
  {
    icon: "ri-bar-chart-grouped-line",
    title: "Proven Results",
    description:
      "Over the years, we have helped numerous clients restore their credit scores and achieve financial success. We are proud of the positive impact we have made on our clients' lives.",
  },
  {
    icon: "ri-user-settings-line",
    title: "Personalized Approach",
    description:
      "We recognize that each individual's credit situation is unique. That is why we tailor our solutions to address your specific needs and goals.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Customer Support",
    description:
      "Our dedicated support team is always available to answer your questions and provide updates on your credit repair progress. Speak to an expert — not a robot.",
  },
];

export default function WhyChooseUsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-us"
      ref={sectionRef}
      className="py-16 md:py-24 bg-primary-50"
    >
      <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-4">
            Why Us
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark-950 mb-4">
            Why Choose Us as Your Credit Repair Company?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-12 md:mb-16">
          {reasons.map((reason, index) => (
            <div
              key={reason.title}
              className={`group p-6 md:p-8 rounded-xl bg-white border border-dark-100 hover:border-primary-200 transition-all duration-500 hover:shadow-lg hover:shadow-primary-100/50 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: visible ? `${index * 120}ms` : "0ms" }}
            >
              <div className="w-14 h-14 rounded-xl bg-primary-600 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                <i className={`${reason.icon} text-white text-2xl`} />
              </div>
              <h3 className="font-heading font-semibold text-xl text-dark-950 mb-3">
                {reason.title}
              </h3>
              <p className="text-dark-400 text-sm leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`max-w-3xl mx-auto text-center transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <blockquote className="text-dark-600 text-base md:text-lg leading-relaxed italic">
            &ldquo;We are committed to empowering our clients to take control of their credit scores. We believe that everyone deserves a second chance and the opportunity to achieve their financial dreams. Our mission is to provide personalized credit repair solutions that are tailored to your unique circumstances and goals.&rdquo;
          </blockquote>
        </div>
      </div>
    </section>
  );
}